// Stap 3: bankafschrift-upload. PRD-06 §4 US-ONB-03.

import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import type { OnboardingProgress } from "../OnboardingWizard";

interface Props {
  progress: OnboardingProgress;
  onBack: () => void;
  onAdvance: () => void;
  onSkip: () => void;
}

export function BankUploadStep({ onBack, onAdvance, onSkip }: Props) {
  const { t } = useTranslation();

  const docsQuery = useQuery({
    queryKey: ["bank-statements"],
    queryFn: () => api.get<{ documents: Array<{ id: string; status: string }> }>("/v1/bank-statements"),
    refetchInterval: 5_000,
  });

  const hasDoc = (docsQuery.data?.documents.length ?? 0) > 0;
  const completed = docsQuery.data?.documents.some((d) => d.status === "COMPLETED");

  return (
    <article className="bg-white rounded-2xl border border-neutral-200 p-6 sm:p-8">
      <h2 className="text-2xl font-semibold mb-2">
        {t("onboarding.bank.title", "Bankrekeningen")}
      </h2>
      <p className="text-neutral-700 mb-4">
        {t(
          "onboarding.bank.intro",
          "Upload je bankafschrift (CSV-export van internetbankieren). Ember herkent automatisch je crediteuren en categoriseert transacties."
        )}
      </p>

      {completed ? (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
          <p className="font-medium text-green-900">✓ {t("onboarding.bank.uploaded", "Afschrift verwerkt")}</p>
        </div>
      ) : hasDoc ? (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
          <p className="text-amber-900 text-sm">
            {t("onboarding.bank.processing", "Verwerken… dit duurt 10-30 seconden.")}
          </p>
        </div>
      ) : (
        <Link
          to="/bank-statements"
          className="block px-4 py-3 mb-6 bg-brand-500 hover:bg-brand-600 text-white text-center font-medium rounded-lg"
        >
          {t("onboarding.bank.connect", "Bankafschrift uploaden →")}
        </Link>
      )}

      <div className="flex gap-3">
        <button
          type="button"
          onClick={onBack}
          className="px-4 py-2.5 text-neutral-700 hover:bg-neutral-100 rounded-lg"
        >
          ← {t("back", "Terug")}
        </button>
        {completed ? (
          <button
            type="button"
            onClick={onAdvance}
            className="flex-1 px-4 py-2.5 bg-brand-500 hover:bg-brand-600 text-white rounded-lg font-medium"
          >
            {t("onboarding.continue", "Doorgaan")} →
          </button>
        ) : (
          <button
            type="button"
            onClick={onSkip}
            className="flex-1 px-4 py-2.5 bg-neutral-100 hover:bg-neutral-200 text-neutral-700 rounded-lg"
          >
            {t("onboarding.skipForNow", "Sla over voor nu")} →
          </button>
        )}
      </div>
    </article>
  );
}
